import { Box, Flex, Text, Heading, Badge, Input } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { AppButton, AppTable, TableRow, TableCell, AppSelect } from "../../components";
import { colors } from "../../theme";
import { useAdminOrders, useMalls } from "../../services/useQueries";
import { useAdminRole } from "../../utils/useAdminRole";
import {
  formatCount,
  formatDateTime,
  formatOMR,
  omanToday,
  orderLabel,
  ORDER_STATUS,
  PAYMENT_METHOD,
  paymentState,
} from "../../utils/format";
import OrderDetailsModal from "./OrderDetailsModal";

// Tabs match the server's status groups; "New" is waiting for the shop.
const TABS = [
  { value: "", label: "All", count: "ALL" },
  { value: "NEW", label: "New", count: "NEW" },
  { value: "IN_PROGRESS", label: "In progress", count: "IN_PROGRESS" },
  { value: "DELIVERED", label: "Delivered", count: "DELIVERED" },
  { value: "CANCELLED", label: "Cancelled", count: "CANCELLED" },
];
const PAGE_SIZE = 20;
const HEADERS = ["Order", "Placed", "Shop", "Customer", "Total", "Payment", "Status"];

// The search runs once typing pauses.
function useDebounced(value, ms = 400) {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

const DateField = ({ label, value, onChange, min, max }) => (
  <Flex align="center" gap={2}>
    <Text fontSize="sm" color={colors.textSecondary}>
      {label}
    </Text>
    <Input
      type="date"
      size="sm"
      w="150px"
      bg="white"
      borderRadius="8px"
      value={value}
      min={min}
      max={max}
      onChange={(e) => onChange(e.target.value)}
      aria-label={`${label} date`}
    />
  </Flex>
);

/**
 * Every order placed in the app (a mall admin sees their mall's), newest first,
 * refreshed every 30 seconds. Read-only: shops accept and run orders in the shop
 * app. A row opens the full order.
 */
const OrdersScreen = () => {
  const { isSuperAdmin } = useAdminRole();
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [mallId, setMallId] = useState();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);
  const [openOrderId, setOpenOrderId] = useState(null);
  const query = useDebounced(search.trim());

  // Any filter change starts again from the first page.
  useEffect(() => setPage(1), [status, query, mallId, from, to]);

  const params = {
    page,
    limit: PAGE_SIZE,
    ...(status ? { status } : {}),
    ...(query ? { search: query } : {}),
    ...(mallId ? { mallId } : {}),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
  };
  const { data, isLoading, isError } = useAdminOrders(params);
  const { data: mallsResponse } = useMalls();
  const malls = Array.isArray(mallsResponse) ? mallsResponse : mallsResponse?.data || mallsResponse?.items || [];

  const orders = data?.data || [];
  const counts = data?.counts || {};
  const pagination = data?.pagination || { page: 1, totalPages: 1, total: 0 };
  const filtered = !!(query || mallId || from || to);
  const today = omanToday();

  const clearFilters = () => {
    setSearch("");
    setMallId(undefined);
    setFrom("");
    setTo("");
  };

  const renderRow = (order) => {
    const shown = ORDER_STATUS[order.status] || { label: order.status, palette: "gray" };
    return (
      <TableRow key={order.id} onClick={() => setOpenOrderId(order.id)}>
        <TableCell fontWeight="600" whiteSpace="nowrap">
          {orderLabel(order.orderNumber)}
        </TableCell>
        <TableCell whiteSpace="nowrap">{formatDateTime(order.createdAt)}</TableCell>
        <TableCell>
          <Text>{order.shop?.name}</Text>
          {isSuperAdmin && order.shop?.mall && (
            <Text fontSize="xs" color={colors.textSecondary}>
              {order.shop.mall}
            </Text>
          )}
        </TableCell>
        <TableCell>
          <Text>{order.buyer?.name}</Text>
          {order.buyer?.phone && (
            <Text fontSize="xs" color={colors.textSecondary}>
              {order.buyer.phone}
            </Text>
          )}
        </TableCell>
        <TableCell fontWeight="600" whiteSpace="nowrap">
          {formatOMR(order.total)}
        </TableCell>
        <TableCell>
          <Text>{PAYMENT_METHOD[order.paymentMethod] || order.paymentMethod}</Text>
          <Text fontSize="xs" color={colors.textSecondary}>
            {paymentState(order)}
          </Text>
        </TableCell>
        <TableCell>
          <Badge colorPalette={shown.palette} px={2} py={1} borderRadius="full" fontSize="xs" whiteSpace="nowrap">
            {shown.label}
          </Badge>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Box>
      <Box mb={6}>
        <Heading size="lg" color={colors.text} mb={1}>
          Orders
        </Heading>
        <Text color={colors.textSecondary}>
          Every order placed in the app, newest first. Shops accept and run their orders in the shop app; this page is
          for looking them up.
        </Text>
      </Box>

      <Flex gap={2} mb={4} flexWrap="wrap">
        {TABS.map((tab) => (
          <AppButton
            key={tab.value || "all"}
            variant={status === tab.value ? "primary" : "secondary"}
            onClick={() => setStatus(tab.value)}
          >
            {tab.label}
            {counts[tab.count] !== undefined ? ` (${formatCount(counts[tab.count])})` : ""}
          </AppButton>
        ))}
      </Flex>

      <Flex gap={3} mb={6} flexWrap="wrap" align="center">
        <Input
          placeholder="Order number, shop, customer name or phone"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          bg="white"
          borderRadius="8px"
          size="sm"
          w={{ base: "100%", md: "320px" }}
          aria-label="Search orders"
        />
        {isSuperAdmin && (
          <AppSelect
            placeholder="All malls"
            options={malls.map((m) => ({ value: m.id, label: m.name }))}
            value={mallId}
            onChange={setMallId}
            width="200px"
          />
        )}
        <DateField label="From" value={from} onChange={setFrom} max={to || today} />
        <DateField label="To" value={to} onChange={setTo} min={from || undefined} max={today} />
        {filtered && (
          <AppButton variant="secondary" onClick={clearFilters}>
            Clear filters
          </AppButton>
        )}
      </Flex>

      <AppTable
        headers={HEADERS}
        data={orders}
        renderRow={renderRow}
        isLoading={isLoading}
        emptyMessage={
          isError
            ? "Couldn't load orders. Check the connection and try again."
            : filtered
              ? "No orders match these filters."
              : "No orders here yet."
        }
        pagination={{ page: pagination.page, totalPages: pagination.totalPages, onPageChange: setPage }}
      />
      {pagination.total > 0 && (
        <Text fontSize="sm" color={colors.textSecondary} mt={3}>
          {formatCount(pagination.total)} {pagination.total === 1 ? "order" : "orders"}
        </Text>
      )}

      <OrderDetailsModal orderId={openOrderId} onClose={() => setOpenOrderId(null)} />
    </Box>
  );
};

export default OrdersScreen;
